import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { rejectBody } from '../../utils/apiError';
import { withBranch } from '../../utils/branchRequest';

const API_URL = '/api/customers';

export const fetchCustomers = createAsyncThunk('customers/fetchAll', async (params, { rejectWithValue }) => {
  try {
    const response = await axios.get(API_URL, { params });
    return response.data;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

export const fetchCustomer = createAsyncThunk('customers/fetchOne', async (id, { rejectWithValue }) => {
  try {
    const response = await axios.get(`${API_URL}/${id}`);
    return response.data;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

export const createCustomer = createAsyncThunk('customers/create', async (data, { rejectWithValue }) => {
  try {
    // __branchId (picked before the form opened) pins the create to that
    // branch and is never sent in the body. Without it the call is unchanged.
    const { __branchId, ...body } = data || {};
    const response = __branchId
      ? await axios.post(API_URL, body, withBranch(__branchId))
      : await axios.post(API_URL, data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(rejectBody(error));
  }
});

export const updateCustomer = createAsyncThunk('customers/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await axios.put(`${API_URL}/${id}`, data);
    return response.data;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

// A customer is never deleted (owner C4): DELETE deactivates, and the way back
// is the reactivate action. Both resolve with the id so the reducer can flip the
// cached row's is_active without a refetch.
export const deactivateCustomer = createAsyncThunk('customers/deactivate', async (id, { rejectWithValue }) => {
  try {
    await axios.delete(`${API_URL}/${id}`);
    return id;
  } catch (error) {
    return rejectWithValue(rejectBody(error));
  }
});

export const reactivateCustomer = createAsyncThunk('customers/reactivate', async (id, { rejectWithValue }) => {
  try {
    await axios.post(`${API_URL}/${id}/reactivate`);
    return id;
  } catch (error) {
    return rejectWithValue(rejectBody(error));
  }
});

const setActive = (state, id, isActive) => {
  const row = state.items.find((item) => item.id === id);
  if (row) row.is_active = isActive;
  if (state.currentCustomer?.id === id) state.currentCustomer.is_active = isActive;
};

const customerSlice = createSlice({
  name: 'customers',
  initialState: {
    items: [],
    currentCustomer: null,
    loading: false,
    error: null,
    pagination: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        // A superseded search abandons its read (Customers.js, PERF-API-01 P09).
        // That is not a failure: the request that replaced it owns `loading`,
        // and "Aborted" must never reach the error banner.
        if (action.meta.aborted) return;
        state.loading = false;
        state.error = action.payload?.error || action.payload?.message || action.error.message;
      })
      .addCase(fetchCustomer.fulfilled, (state, action) => {
        state.currentCustomer = action.payload.data;
      })
      .addCase(createCustomer.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
      })
      .addCase(createCustomer.rejected, (state, action) => {
        state.error = action.payload?.error || action.payload?.message || action.error.message;
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        const index = state.items.findIndex((item) => item.id === action.payload.data.id);
        if (index !== -1) {
          state.items[index] = action.payload.data;
        }
      })
      .addCase(deactivateCustomer.fulfilled, (state, action) => {
        setActive(state, action.payload, false);
      })
      .addCase(reactivateCustomer.fulfilled, (state, action) => {
        setActive(state, action.payload, true);
      });
  },
});

export default customerSlice.reducer;
